import { useEffect, useRef } from 'react';
import { useAssistantContext, type DataChange } from './index';

/**
 * Re-read this page when the assistant writes something it shows.
 *
 * A page passes what it cares about; anything it does not match is ignored, so
 * a journal open on 2026-08-14 does not refetch because a forecast version
 * changed, and a page for another property never refetches at all. Filtering
 * here rather than in each page keeps the "does this concern me" rule in one
 * place — the rule that stops an assistant write from reloading the whole app.
 *
 * The handler is held in a ref so a page can pass an inline function without
 * resubscribing on every render.
 */
export function useAssistantDataRefresh(
  refresh: (change: DataChange) => void,
  options: {
    /** Proposal kinds this page displays. Omit to accept any. */
    kinds?: string[];
    /** Only refresh for this property. */
    locationId?: number | string | null;
    /** Only refresh when one of these business dates was touched. */
    dates?: string[];
    /** Only refresh for this forecast version. */
    versionId?: number | null;
    /** Set false to stop listening (e.g. while the page is loading). */
    enabled?: boolean;
  } = {},
) {
  const { onDataChanged } = useAssistantContext();
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const { kinds, locationId, dates, versionId, enabled = true } = options;
  // Serialized so an inline array literal does not resubscribe every render.
  const kindsKey = kinds ? kinds.join(',') : '';
  const datesKey = dates ? dates.join(',') : '';

  useEffect(() => {
    if (!enabled) return undefined;
    return onDataChanged((change) => {
      if (kindsKey) {
        const wanted = kindsKey.split(',');
        if (!(change.kinds || []).some(k => wanted.includes(k))) return;
      }
      if (locationId != null && change.locationId != null
        && String(change.locationId) !== String(locationId)) return;
      if (versionId != null && change.versionId != null && change.versionId !== versionId) return;
      // A change with no dates is not day-scoped, so a date-filtered page still
      // takes it: "something in this version moved" concerns a journal day too.
      if (datesKey && change.dates && change.dates.length > 0) {
        const wanted = datesKey.split(',');
        if (!change.dates.some(d => wanted.includes(d))) return;
      }
      refreshRef.current(change);
    });
  }, [onDataChanged, kindsKey, datesKey, locationId, versionId, enabled]);
}
